/**
 * 按部署实例切换品牌资源（public/branding/<slug>/）。
 * 构建时通过 REACT_APP_BRANDING_SLUG 选择目录；未设置时与 Genechamp 历史行为一致。
 */
import frontendPackageJson from '../../package.json';

function sanitizeBrandingSlug(raw) {
	const s = (raw || 'pringxsoft').trim().toLowerCase();
	if (!/^[a-z0-9_-]+$/.test(s)) return 'pringxsoft';
	return s;
}

const brandingSlug = sanitizeBrandingSlug(process.env.REACT_APP_BRANDING_SLUG);
const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const base = `${publicUrl}/branding/${brandingSlug}`;
const rootBase = publicUrl || '';

export const brandingUrls = {
	login: `${base}/login.png`,
	logoMark: brandingSlug === 'genechamp' ? `${base}/logo-mark-figma.svg` : `${base}/exports/figma-181-2-20260917-051459/mark.clean.svg`,
	wordmarkPrimary: `${base}/wordmark-primary.png`,
	wordmarkWhite: `${base}/wordmark-white.png`,
};

/**
 * 站点名称与 favicon：
 * - REACT_APP_SITE_TITLE：浏览器标签页标题（与登录页主标题独立）
 * - REACT_APP_FAVICON_URL：favicon 完整路径（可指向 branding/<slug>/...）
 */
export const siteTitle = (process.env.REACT_APP_SITE_TITLE || '').trim() || 'PringXSoft ERP ｜ 派瓴软件 · 更好用的企业级管理软件';
export const faviconUrl = process.env.REACT_APP_FAVICON_URL || `${rootBase}/favicon.ico`;

/**
 * 登录页主标题（页面中展示的系统名称），与 siteTitle 分开配置。
 * REACT_APP_LOGIN_PRODUCT_TITLE；未配置时用默认产品名，不回退到 siteTitle。
 */
export const loginProductTitle = (process.env.REACT_APP_LOGIN_PRODUCT_TITLE || '').trim() || 'PringXSoft ERP';

/** 登录页副标题/公司名（可选）；未配置则不展示 */
export const loginCompanyName = (process.env.REACT_APP_LOGIN_COMPANY || '').trim();

function envText(key) {
	return (process.env[key] || '').trim();
}

const isJiecaiTenant = brandingSlug === 'genechamp' || loginCompanyName.includes('捷采');

/**
 * 报价单等打印页公司地址（可选）。多行用 | 分隔。
 * 例：中国, 上海市, 徐汇区|龙启路258号, 绿地汇中心 B座 2119室
 */
export const loginCompanyAddressLines = (() => {
	const raw = envText('REACT_APP_LOGIN_COMPANY_ADDRESS');
	if (raw) {
		return raw
			.split('|')
			.map((line) => line.trim())
			.filter(Boolean);
	}
	if (isJiecaiTenant) {
		return ['中国, 上海市, 徐汇区', '龙启路258号, 绿地汇中心 B座 2119室'];
	}
	return [];
})();

/** 捷采公开联系信息；仅捷采实例且未配环境变量时回退 */
const genechampCompanyExtras = {
	phone: '021-64386875',
	email: 'business@genechamp.com',
	website: 'www.genechamp.com',
	nameEn: 'Shanghai Genechamp Biotech Co., Ltd.',
};

function companyExtra(envKey, fallbackKey) {
	const fromEnv = envText(envKey);
	if (fromEnv) return fromEnv;
	if (isJiecaiTenant) return genechampCompanyExtras[fallbackKey] || '';
	return '';
}

/** 报价单抬头：英文名 / 电话 / 邮箱 / 网址 / 传真（可选；捷采实例有公开信息回退） */
export const loginCompanyNameEn = companyExtra('REACT_APP_LOGIN_COMPANY_NAME_EN', 'nameEn');
export const loginCompanyPhone = companyExtra('REACT_APP_LOGIN_COMPANY_PHONE', 'phone');
export const loginCompanyEmail = companyExtra('REACT_APP_LOGIN_COMPANY_EMAIL', 'email');
export const loginCompanyWebsite = companyExtra('REACT_APP_LOGIN_COMPANY_WEBSITE', 'website');
export const loginCompanyFax = envText('REACT_APP_LOGIN_COMPANY_FAX');

/** 报价单收款信息（可选，无回退，避免写错税号/账号） */
export const loginCompanyTaxId = envText('REACT_APP_LOGIN_COMPANY_TAX_ID');
export const loginCompanyBank = envText('REACT_APP_LOGIN_COMPANY_BANK');
export const loginCompanyAccount = envText('REACT_APP_LOGIN_COMPANY_ACCOUNT');

/** 登录页 Typewriter 文案，| 分隔；未配置则使用 PringXSoft 默认 */
export const loginTaglines = (() => {
	const fallback = ['PringXSoft® ERP', '派瓴软件® · 更好用的企业级管理软件'];
	const raw = process.env.REACT_APP_LOGIN_TAGLINES;
	if (raw && raw.trim()) {
		const parsed = raw
			.split('|')
			.map((t) => t.trim())
			.filter(Boolean);
		if (parsed.length) return parsed;
	}
	return fallback;
})();

/** 首页 / 工作台页脚品牌文案（与租户 siteTitle 分离） */
export const productBrandFooterProduct = process.env.REACT_APP_PRODUCT_BRAND_FOOTER_PRODUCT || 'PringXSoft ERP';
export const productBrandFooterLabel = process.env.REACT_APP_PRODUCT_BRAND_FOOTER_LABEL || '派瓴软件';

/**
 * 登录页页脚统一为派瓴软件（不随租户切换）。
 * ICP 可用 REACT_APP_LOGIN_FOOTER_ICP 覆盖；REACT_APP_LOGIN_FOOTER_LEGAL 仅兼容旧引用。
 */
export const loginFooterBrand = '派瓴软件®版权所有';
export const loginFooterIcp = (process.env.REACT_APP_LOGIN_FOOTER_ICP || '').trim() || '沪ICP备2024087207号';
export const loginFooterLegal = process.env.REACT_APP_LOGIN_FOOTER_LEGAL || `${loginFooterBrand} | ${loginFooterIcp}`;

/** 构建写入的版本（与根目录发版号对齐） */
export const appVersion = frontendPackageJson.version;

/** 登录页 / 首页页脚版本号：发版时 npm version 同步 package.json，无需再配环境变量 */
export function appVersionLabel() {
	return `v${appVersion}`;
}

/** 确保注册商标名带 ®（已有则不重复追加） */
function ensureRegisteredMark(label, { pringxsoftProduct = false } = {}) {
	const value = String(label || '').trim();
	if (!value) return value;
	if (value.includes('®')) return value;
	if (pringxsoftProduct) {
		return value.replace(/^(PringXSoft)(?!®)/i, 'PringXSoft®');
	}
	return `${value}®`;
}

/** 页脚产品名：PringXSoft® ERP */
export function brandProductFooterMarked() {
	return ensureRegisteredMark(productBrandFooterProduct, { pringxsoftProduct: true });
}

/** 页脚：Thank you for creating with PringXSoft® ERP（对齐 Falcon） */
export function brandThanksUsingLabel() {
	return `Thank you for creating with ${brandProductFooterMarked()}`;
}

/** 页脚：2026 © 派瓴软件® */
export function brandCopyrightLabel(year = new Date().getFullYear()) {
	return `${year} © ${ensureRegisteredMark(productBrandFooterLabel)}`;
}

export function loginCopyrightLine() {
	const year = new Date().getFullYear();
	return `${year} © ${appVersionLabel()}`;
}

export function applyBrandingToDocument() {
	if (typeof document === 'undefined') return;

	document.title = siteTitle;

	const iconLink = document.querySelector("link[rel='shortcut icon']") || document.querySelector("link[rel='icon']");
	if (iconLink) {
		iconLink.setAttribute('href', faviconUrl);
	}
}

export { brandingSlug };
